import { FormikProps } from 'formik';
import React, { useRef, useState } from 'react';
import { Icon, Popup } from 'semantic-ui-react';
import * as S from './styles';
import { fileTypeProps } from '../../../../../../components/UploadFilesUncontroled';
import { CreateClaimEstateInitialInitialValuesType } from '../../Formik/types/CreateClaimEstateInitialValuesTypes';
import { InsurerState } from '../../../../../../types/InsurerState';
import { ClaimPropertyStatusEnum } from '../../../../../../enums/claim-property-status.enum';
import { warningNotification } from '../../../../../../services/notification';

type Properties = {
  fileIdentifiers: fileTypeProps[];
  insurers?: InsurerState[] | null;
  existingFiles?: any[];
  onDeleteExisting?: (id: string) => void;
};

const DocumentsComponent = ({
  fileIdentifiers,
  values,
  setFieldValue,
  insurers,
  existingFiles,
  onDeleteExisting
}: FormikProps<CreateClaimEstateInitialInitialValuesType> & Properties) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeUploadKey, setActiveUploadKey] = useState<string | null>(null);

  const handleUploadClick = (key: string) => {
    setActiveUploadKey(key);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleDeleteClick = (key: string, indexToDelete: number) => {
    if (window.confirm('Tem certeza que deseja remover este arquivo?')) {
      const filesForKey = values.claimFiles.filter(f => f.name === key);
      const otherFiles = values.claimFiles.filter(f => f.name !== key);
      const newFilesForKey = filesForKey.filter((_, index) => index !== indexToDelete);
      setFieldValue('claimFiles', [...otherFiles, ...newFilesForKey]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && activeUploadKey) {
      const file = files[0];
      if (file.size > 50 * 1024 * 1024) {
        warningNotification(
          'Arquivo muito grande',
          'O arquivo excede o limite de 50MB.'
        );
        return;
      }

      const newFileEntry = { file, name: activeUploadKey };
      setFieldValue('claimFiles', [...values.claimFiles, newFileEntry]);
    }
    setActiveUploadKey(null);
  };

  const getFilesByKey = (key: string) => {
    return values.claimFiles.filter((f) => f.name === key);
  };

  const getOccupationLabel = () => {
    if (values.claimProperty.status === ClaimPropertyStatusEnum.occupied)
      return 'Ocupado';
    if (values.claimProperty.status === ClaimPropertyStatusEnum.vacant)
      return 'Desocupado';
    return '-';
  };

  const getInsurerName = () => {
    if (values.claimEstate.insurerId && insurers) {
      const insurer = insurers.find((i) => i.id === values.claimEstate.insurerId);
      if (insurer) return insurer.name;
    }
    return '[Nome da seguradora]';
  };

  return (
    <S.Container>
      <S.Header>
        <S.TitleBlock>
          <S.Title>
            A inserção de documentos é obrigatória para abertura do Sinistro.
          </S.Title>
          <S.SubTitle>
            Verifique a lista de documentos abaixo para enviar corretamente os
            documentos exigidos de acordo com a ocupação do imóvel e Seguradora
            selecionada. São aceitos arquivos de até 50MB. Formatos aceitos:
            jpeg, png, webp, doc, docx, PDF e odt
            <br />
            <b>Nota:</b> Você pode adicionar múltiplos arquivos para cada tipo de documento.
          </S.SubTitle>
        </S.TitleBlock>
        <S.InfoBanner>
          <Icon name="info circle" />
          <span>
            Caso não tenha acesso a toda a documentação, você pode seguir com a{' '}
            <b>criação do processo de Sinistro no formato de Rascunho</b> e
            realizar o upload da documentação em outro momento
          </span>
        </S.InfoBanner>
      </S.Header>

      <S.SectionWithBorder>
        <S.InfoSection>
          <S.InfoColumn>
            <S.InfoLabel>Status de ocupação do imóvel</S.InfoLabel>
            <S.InfoValueBox>
              <S.InfoValueText>{getOccupationLabel()}</S.InfoValueText>
            </S.InfoValueBox>
          </S.InfoColumn>
          <S.InfoColumn>
            <S.InfoLabel>Seguradora/Garantidora</S.InfoLabel>
            <S.InfoValueBox>
              <S.InfoValueText>{getInsurerName()}</S.InfoValueText>
            </S.InfoValueBox>
          </S.InfoColumn>
        </S.InfoSection>

        <S.DocumentsListContainer>
          <S.ListHeader>
            <S.ListHeaderLabel className="col-name">
              <Icon name="fire" style={{ color: '#59249B' }} />
              Arquivos necessários
            </S.ListHeaderLabel>
            <S.ListHeaderLabel className="col-status">Status</S.ListHeaderLabel>
            <S.ListHeaderLabel className="col-actions">Ações</S.ListHeaderLabel>
          </S.ListHeader>

          {fileIdentifiers.map((item) => {
            const uploadedFiles = getFilesByKey(item.key);
            const currentExistingFiles = existingFiles?.filter(f => f.key === item.key) || [];
            const hasFiles = uploadedFiles.length > 0 || currentExistingFiles.length > 0;
            const isRequired = item.required === true;

            return (
              <React.Fragment key={item.key}>
                {currentExistingFiles.map((existingFile, index) => (
                  <S.ListItem key={`existing-${existingFile.id}`}>
                    <S.DocumentName>
                      <S.DocumentIconWrapper>
                        <Icon name="file text outline" style={{ color: '#59249B' }} />
                      </S.DocumentIconWrapper>
                      <a href={existingFile.url} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                        {item.name} (Anexado - {index + 1})
                      </a>
                      {isRequired && <S.RequiredAsterisk>*</S.RequiredAsterisk>}
                    </S.DocumentName>
                    <S.StatusWrapper>
                      <S.StatusBadge status="sent">
                        <S.StatusDot status="sent" />
                        <S.StatusText>Anexado</S.StatusText>
                      </S.StatusBadge>
                    </S.StatusWrapper>
                    <S.ActionsWrapper>
                      <S.ActionButton
                        actionType="delete"
                        onClick={() => onDeleteExisting && onDeleteExisting(existingFile.id)}
                        title="Remover arquivo salvo"
                      >
                        <Icon name="times" style={{ margin: 0 }} />
                      </S.ActionButton>
                    </S.ActionsWrapper>
                  </S.ListItem>
                ))}

                {uploadedFiles.map((uploadedFile, index) => (
                  <S.ListItem key={`${item.key}-${index}`}>
                    <S.DocumentName>
                      <S.DocumentIconWrapper>
                        <Icon name="file text outline" style={{ color: '#59249B' }} />
                      </S.DocumentIconWrapper>
                      {item.name} ({index + 1})
                      {isRequired && <S.RequiredAsterisk>*</S.RequiredAsterisk>}
                    </S.DocumentName>
                    <S.StatusWrapper>
                      <S.StatusBadge status="sent">
                        <S.StatusDot status="sent" />
                        <S.StatusText>Enviado</S.StatusText>
                      </S.StatusBadge>
                    </S.StatusWrapper>
                    <S.ActionsWrapper>
                      <S.ActionButton
                        actionType="delete"
                        onClick={() => handleDeleteClick(item.key, index)}
                        title="Remover arquivo"
                      >
                        <Icon name="times" style={{ margin: 0 }} />
                      </S.ActionButton>
                    </S.ActionsWrapper>
                  </S.ListItem>
                ))}

                <S.ListItem key={`${item.key}-add`}>
                  <S.DocumentName>
                    <S.DocumentIconWrapper>
                      <Icon name="file text outline" style={{ color: hasFiles ? '#59249B' : (isRequired ? '#FF3B30' : '#999') }} />
                    </S.DocumentIconWrapper>
                    {item.name} {hasFiles ? '(Adicionar Novo)' : ''}
                    {isRequired && <S.RequiredAsterisk>*</S.RequiredAsterisk>}
                    {item.tooltip && (
                      <Popup
                        content={item.tooltip}
                        position="right center"
                        trigger={
                          <Icon
                            name="info circle"
                            style={{ marginLeft: 8, color: '#59249B', cursor: 'help' }}
                          />
                        }
                      />
                    )}
                  </S.DocumentName>
                  <S.StatusWrapper>
                    {!hasFiles && (
                      <S.StatusBadge status="pending">
                        <S.StatusDot status="pending" />
                        <S.StatusText>{isRequired ? 'Obrigatório' : 'Pendente'}</S.StatusText>
                      </S.StatusBadge>
                    )}
                  </S.StatusWrapper>
                  <S.ActionsWrapper>
                    <S.ActionButton
                      actionType="upload"
                      onClick={() => handleUploadClick(item.key)}
                      title="Enviar arquivo"
                    >
                      <Icon name="upload" style={{ margin: 0 }} />
                    </S.ActionButton>
                  </S.ActionsWrapper>
                </S.ListItem>
              </React.Fragment>
            );
          })}
        </S.DocumentsListContainer>
      </S.SectionWithBorder>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
        accept=".jpeg,.jpg,.png,.webp,.doc,.docx,.pdf,.odt"
      />
    </S.Container>
  );
};
export default DocumentsComponent;
